import { useMemo, useState } from "react"
import { Avatar, Button, Dropdown, Space, Tooltip } from "antd"
import { DownOutlined, RightOutlined, UserAddOutlined, PlusOutlined } from "@ant-design/icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faEllipsisV, faRoute } from "@fortawesome/pro-solid-svg-icons"
import { faSparkles } from "@fortawesome/pro-regular-svg-icons/faSparkles"
import { CalendarEventVisit, CrewMember, DayViewEvent, Timesheet, Visit, VisitWorkareaStatusRecord, ConsultationCalendarEvent, TaskCalendarEvent } from "../scheduleApi"
import DayViewEventGroup from "./DayViewEventGroup"
import dayjs, { Dayjs } from "dayjs"
import { eventHasVisit, isEventClockedIn, getInitials, getDayDateRange } from "../scheduleModel"
import { hasSufficientLocations } from "../OptimizeSchedule/optimizeScheduleModel"
import { useSchedule } from "../ScheduleContext"
import { useRemoveScheduledEventMutation } from "../scheduleQueries"
import CreateEventDrawer from "../CreateEvent/CreateEventDrawer"
import CrewLeadNotesPopover from "./CrewLeadNotesPopover"

type Props = {
  crewLead: CrewMember | null
  events: DayViewEvent[]
  consultations: ConsultationCalendarEvent[]
  tasks?: TaskCalendarEvent[]
  visits: Visit[]
  calendarEventVisits: CalendarEventVisit[]
  visitWorkareaStatuses: VisitWorkareaStatusRecord[]
  timesheets: Timesheet[]
  date: Dayjs
  hoveredEventId: number | null
  hoveredEventIds: number[]
  setHoveredEventId: (id: number | null) => void
  setHoveredEventIds: (ids: number[]) => void
  onConsultationClick?: (consultation: ConsultationCalendarEvent) => void
  onTaskClick?: (event: TaskCalendarEvent) => void
  onEnterReorderMode: () => void
  isFirst?: boolean
}

const DayViewCrewLeadSection = ({
  crewLead,
  events,
  consultations,
  tasks = [],
  visits,
  calendarEventVisits,
  visitWorkareaStatuses,
  timesheets,
  date,
  hoveredEventId,
  hoveredEventIds,
  setHoveredEventId,
  setHoveredEventIds,
  onConsultationClick,
  onTaskClick,
  onEnterReorderMode,
}: Props) => {
  const { crew } = useSchedule()
  const [isExpanded, setIsExpanded] = useState(false)
  const [createEventDrawerOpen, setCreateEventDrawerOpen] = useState(false)
  const [createEventDrawerTab, setCreateEventDrawerTab] = useState<string>('1')
  const [hoveredConsultationId, setHoveredConsultationId] = useState<number | null>(null)
  const { mutate: removeScheduledEvent } = useRemoveScheduledEventMutation()

  const uniqueRoutes = useMemo(() => {
    const routeMap = new Map<number, { id: number; name: string }>()
    events.forEach(event => {
      if (event.routeId && event.routeName) {
        routeMap.set(event.routeId, { id: event.routeId, name: event.routeName })
      }
    })
    return Array.from(routeMap.values())
  }, [events])

  const clockedInEvents = events.filter((event) => {
    return isEventClockedIn(event, calendarEventVisits, visits, timesheets, crew ?? [], getDayDateRange(date))
  })

  const plannedEvents = events.filter((event) => !eventHasVisit(event, calendarEventVisits, visits, getDayDateRange(date)))

  const visitedEvents = events.filter((event) => {
    const hasVisit = eventHasVisit(event, calendarEventVisits, visits, getDayDateRange(date))
    const isClockedIn = isEventClockedIn(event, calendarEventVisits, visits, timesheets, crew ?? [], getDayDateRange(date))
    return hasVisit && !isClockedIn
  })

  const hasItems = clockedInEvents.length > 0 || plannedEvents.length > 0 || visitedEvents.length > 0 || consultations.length > 0 || tasks.length > 0
  const canReorder = hasSufficientLocations(plannedEvents)

  if (!hasItems) {
    return null
  }

  const getStatusText = () => {
    if (clockedInEvents.length > 0) {
      return `Currently at ${clockedInEvents[0].customerDisplayName || 'Unknown'}`
    }
    if (visitedEvents.length > 0) {
      const visitedJobIds = visitedEvents.map(e => e.jobId)
      const relevantTimesheets = timesheets.filter(t => visitedJobIds.includes(t.projectId) && t.crewMemberId === crewLead?.id)

      if (relevantTimesheets.length > 0) {
        const sortedByStart = [...relevantTimesheets].sort((a, b) => dayjs(a.startDateUtc).valueOf() - dayjs(b.startDateUtc).valueOf())
        const sortedByEnd = [...relevantTimesheets].sort((a, b) => dayjs(b.endDateUtc).valueOf() - dayjs(a.endDateUtc).valueOf())
        const firstStart = dayjs.utc(sortedByStart[0].startDateUtc).local().format('h:mm A')
        const lastEnd = dayjs.utc(sortedByEnd[0].endDateUtc).local().format('h:mm A')
        return `${firstStart} - ${lastEnd}`
      }

      const sortedByStart = [...visitedEvents].sort((a, b) => dayjs(a.start).valueOf() - dayjs(b.start).valueOf())
      const sortedByEnd = [...visitedEvents].sort((a, b) => dayjs(b.end).valueOf() - dayjs(a.end).valueOf())
      const firstStart = dayjs(sortedByStart[0].start).format('h:mm A')
      const lastEnd = dayjs(sortedByEnd[0].end).format('h:mm A')
      return `${firstStart} - ${lastEnd}`
    }
    if (plannedEvents.length > 0) {
      const timedEvents = plannedEvents.filter(e => !e.allDay)
      if (timedEvents.length > 0) {
        const sortedByStart = [...timedEvents].sort((a, b) => dayjs(a.start).valueOf() - dayjs(b.start).valueOf())
        return `Starts at ${dayjs(sortedByStart[0].start).format('h:mm A')}`
      }
      const sortedByStart = [...plannedEvents].sort((a, b) => dayjs(a.start).valueOf() - dayjs(b.start).valueOf())
      return `Starts at ${sortedByStart[0].customerDisplayName}`
    }
    return ''
  }

  const crewLeadColor = crewLead ? (crewLead.color || '#000000') : '#999'

  return (
    <div style={{
      margin: '0 12px 8px 12px',
      border: '1px solid #e8e8e8',
      borderRadius: '8px',
      overflow: 'hidden'
    }}>
      <div
        onClick={() => setIsExpanded(!isExpanded)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          padding: '12px 20px',
          backgroundColor: '#fff',
          cursor: 'pointer',
          userSelect: 'none'
        }}
      >
        <span style={{ color: '#666', fontSize: '18px' }}>
          {isExpanded ? <DownOutlined /> : <RightOutlined />}
        </span>
        <div style={{ position: 'relative' }}>
          {crewLead ? (
            <Tooltip title={crewLead.userEmail}>
              {crewLead.avatar && !crewLead.hasStockAvatar ? (
                <Avatar size={64} src={crewLead.avatar} style={{ border: `5px solid ${crewLeadColor}` }} />
              ) : (
                <div style={{
                  width: '64px',
                  height: '64px',
                  borderRadius: '50%',
                  backgroundColor: crewLeadColor,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}>
                  <div style={{
                    width: '54px',
                    height: '54px',
                    borderRadius: '50%',
                    backgroundColor: 'rgba(255, 255, 255, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <div style={{
                      width: '36px',
                      height: '36px',
                      borderRadius: '50%',
                      backgroundColor: crewLeadColor,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '14px',
                      fontWeight: 600,
                      color: '#fff'
                    }}>
                      {getInitials(crewLead.name)}
                    </div>
                  </div>
                </div>
              )}
            </Tooltip>
          ) : (
            <Avatar size={64} icon={<UserAddOutlined />} style={{ backgroundColor: '#FFF1F0', border: '2px dashed #820014', color: '#820014' }} />
          )}
          {clockedInEvents.length > 0 && (
            <div
              style={{
                position: 'absolute',
                top: 2,
                right: 2,
                width: 16,
                height: 16,
                backgroundColor: '#52c41a',
                borderRadius: '50%',
                border: '2px solid #fff'
              }}
            />
          )}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '15px', fontWeight: 600, color: '#333' }}>
            {crewLead ? crewLead.name : 'Unassigned'}
          </div>
          <div style={{ fontSize: '13px', color: '#999' }}>
            {getStatusText()}
          </div>
        </div>
        {canReorder && (
          <Tooltip title="Optimize Planned Events">
            <Button
              type="text"
              icon={<FontAwesomeIcon icon={faSparkles} />}
              onClick={(e) => {
                e.stopPropagation()
                onEnterReorderMode()
              }}
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '8px',
                backgroundColor: '#fff',
                border: '1px solid #d9d9d9',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            />
          </Tooltip>
        )}
        <CrewLeadNotesPopover
          events={events}
          visits={visits}
          calendarEventVisits={calendarEventVisits}
          visitWorkareaStatuses={visitWorkareaStatuses}
          timesheets={timesheets}
          date={date}
        />
        <Dropdown
          trigger={['click']}
          menu={{
            items: [
              {
                key: 'create-event',
                label: <Space><PlusOutlined />Add Event</Space>,
                onClick: (e) => {
                  e.domEvent.stopPropagation()
                  setCreateEventDrawerTab('1')
                  setCreateEventDrawerOpen(true)
                }
              },
              {
                key: 'add-route',
                label: <Space><FontAwesomeIcon icon={faRoute} />Add Route</Space>,
                onClick: (e) => {
                  e.domEvent.stopPropagation()
                  setCreateEventDrawerTab('2')
                  setCreateEventDrawerOpen(true)
                }
              },
              ...(uniqueRoutes.length > 0 ? [{ type: 'divider' as const, key: 'divider' }] : []),
              ...uniqueRoutes.map(route => ({
                key: `remove-route-${route.id}`,
                label: <Space><FontAwesomeIcon icon={faRoute} />Remove {route.name} Events</Space>,
                onClick: (e: { domEvent: { stopPropagation: () => void } }) => {
                  e.domEvent.stopPropagation()
                  const plannedEventsForRoute = events.filter(event =>
                    event.routeId === route.id && !eventHasVisit(event, calendarEventVisits, visits, getDayDateRange(date))
                  )
                  plannedEventsForRoute.forEach(event => {
                    removeScheduledEvent(event.id)
                  })
                }
              }))
            ]
          }}
        >
          <Button
            type="text"
            icon={<FontAwesomeIcon icon={faEllipsisV} />}
            onClick={e => e.stopPropagation()}
          />
        </Dropdown>
      </div>

      {isExpanded && (
        <div>
          {clockedInEvents.length > 0 ? (
            <>
              <DayViewEventGroup
                title="Current"
                events={clockedInEvents}
                workareaStatuses={visitWorkareaStatuses}
                timesheets={timesheets}
                visits={visits}
                calendarEventVisits={calendarEventVisits}
                date={date}
                hoveredEventId={hoveredEventId}
                hoveredEventIds={hoveredEventIds}
                setHoveredEventId={setHoveredEventId}
                setHoveredEventIds={setHoveredEventIds}
                defaultExpanded={true}
              />
              <DayViewEventGroup
                title="Planned"
                events={plannedEvents}
                consultations={consultations}
                tasks={tasks}
                workareaStatuses={visitWorkareaStatuses}
                timesheets={timesheets}
                visits={visits}
                calendarEventVisits={calendarEventVisits}
                date={date}
                hoveredEventId={hoveredEventId}
                hoveredEventIds={hoveredEventIds}
                setHoveredEventId={setHoveredEventId}
                setHoveredEventIds={setHoveredEventIds}
                hoveredConsultationId={hoveredConsultationId}
                setHoveredConsultationId={setHoveredConsultationId}
                onConsultationClick={onConsultationClick}
                onTaskClick={onTaskClick}
                showTopBorder={true}
              />
              <DayViewEventGroup
                title="Visited"
                events={visitedEvents}
                workareaStatuses={visitWorkareaStatuses}
                timesheets={timesheets}
                visits={visits}
                calendarEventVisits={calendarEventVisits}
                date={date}
                hoveredEventId={hoveredEventId}
                hoveredEventIds={hoveredEventIds}
                setHoveredEventId={setHoveredEventId}
                setHoveredEventIds={setHoveredEventIds}
                showTopBorder={true}
              />
            </>
          ) : visitedEvents.length > 0 ? (
            <>
              <DayViewEventGroup
                title="Visited"
                events={visitedEvents}
                workareaStatuses={visitWorkareaStatuses}
                timesheets={timesheets}
                visits={visits}
                calendarEventVisits={calendarEventVisits}
                date={date}
                hoveredEventId={hoveredEventId}
                hoveredEventIds={hoveredEventIds}
                setHoveredEventId={setHoveredEventId}
                setHoveredEventIds={setHoveredEventIds}
                defaultExpanded={true}
              />
              <DayViewEventGroup
                title="Planned"
                events={plannedEvents}
                consultations={consultations}
                tasks={tasks}
                workareaStatuses={visitWorkareaStatuses}
                timesheets={timesheets}
                visits={visits}
                calendarEventVisits={calendarEventVisits}
                date={date}
                hoveredEventId={hoveredEventId}
                hoveredEventIds={hoveredEventIds}
                setHoveredEventId={setHoveredEventId}
                setHoveredEventIds={setHoveredEventIds}
                hoveredConsultationId={hoveredConsultationId}
                setHoveredConsultationId={setHoveredConsultationId}
                onConsultationClick={onConsultationClick}
                onTaskClick={onTaskClick}
                showTopBorder={true}
              />
            </>
          ) : (
            <DayViewEventGroup
              title="Planned"
              events={plannedEvents}
              consultations={consultations}
              tasks={tasks}
              workareaStatuses={visitWorkareaStatuses}
              timesheets={timesheets}
              visits={visits}
              calendarEventVisits={calendarEventVisits}
              date={date}
              hoveredEventId={hoveredEventId}
              hoveredEventIds={hoveredEventIds}
              setHoveredEventId={setHoveredEventId}
              setHoveredEventIds={setHoveredEventIds}
              hoveredConsultationId={hoveredConsultationId}
              setHoveredConsultationId={setHoveredConsultationId}
              onConsultationClick={onConsultationClick}
              onTaskClick={onTaskClick}
              defaultExpanded={true}
            />
          )}
        </div>
      )}

      <CreateEventDrawer
        open={createEventDrawerOpen}
        onClose={() => setCreateEventDrawerOpen(false)}
        selectedDate={date}
        initialTab={createEventDrawerTab}
      />
    </div>
  )
}

export default DayViewCrewLeadSection
